import React, { useState, useEffect } from 'react';
import { chatService } from '../../services/chatService';

const KnowledgeTextInput: React.FC = () => {
  const today = new Date().toISOString().slice(0, 10);
  const [text, setText] = useState('');
  const [tagFields, setTagFields] = useState<Record<string, string[]>>({});
  const [selectedValues, setSelectedValues] = useState<Record<string, string>>({});
  const [defaultTagField, setDefaultTagField] = useState('RAG_tag');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [asOfDate, setAsOfDate] = useState(today);

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const meta = await chatService.getChatMeta();
        const fields: Record<string, string[]> = meta.tag_fields || {};
        setTagFields(fields);
        setDefaultTagField(meta.default_tag_field || 'RAG_tag');

        const initialValues: Record<string, string> = {};
        Object.entries(fields).forEach(([field, values]) => {
          if (Array.isArray(values) && values.length > 0) {
            initialValues[field] = values[0];
          }
        });
        setSelectedValues(initialValues);
      } catch {
        // fallback
      }
    };
    loadMeta();
  }, []);

  const handleSubmit = async () => {
    const ragTagValue = selectedValues[defaultTagField];
    if (!text.trim() || !ragTagValue) return;
    setSubmitting(true);
    setSuccess('');
    setError('');
    try {
      await chatService.addKnowledge({
        text,
        tag: ragTagValue,
        type: selectedValues.type || 'N/A',
        metadata_tags: selectedValues,
        as_of_date: asOfDate,
      });
      setSuccess('Knowledge added successfully');
      setText('');
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to add knowledge');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-gray-700">Add Text Knowledge</h3>

      <div className="grid grid-cols-2 gap-4">
        {Object.entries(tagFields).map(([field, values]) => (
          <div key={field}>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              {field === defaultTagField ? `${field} *` : field}
            </label>
            <select
              value={selectedValues[field] || ''}
              onChange={(e) =>
                setSelectedValues((prev) => ({
                  ...prev,
                  [field]: e.target.value,
                }))
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              {values.map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>
        ))}
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">As of date *</label>
        <input
          type="date"
          value={asOfDate}
          onChange={(e) => setAsOfDate(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Knowledge Text *</label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={6}
          placeholder="Paste or type knowledge content here..."
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
      </div>

      <button
        onClick={handleSubmit}
        disabled={submitting || !text.trim() || !selectedValues[defaultTagField]}
        className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        {submitting ? 'Adding...' : 'Add Knowledge'}
      </button>

      {success && <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">{success}</div>}
      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</div>}
    </div>
  );
};

export default KnowledgeTextInput;
